import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";
import { User, Mail, Lock, Loader2, ShieldCheck, Sparkles, Check, CreditCard, AlertTriangle } from "lucide-react";

export default function Account() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const isPaid = user?.subscription_status === "active";
  const isGoogle = user?.auth_provider === "google";
  const willCancel = user?.subscription_cancel_at_period_end;

  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    if (!user?.is_paid) {
      (async () => { try { await api.post("/payments/reconcile"); await refreshUser(); } catch {} })();
    }
  }, []);

  const saveName = async () => {
    if (!name.trim()) { toast.error("Name cannot be empty"); return; }
    setSavingName(true);
    try {
      await api.patch("/auth/profile", { name: name.trim() });
      await refreshUser();
      toast.success("Name updated");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not update"); }
    finally { setSavingName(false); }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not change password"); }
    finally { setSavingPw(false); }
  };

  const cancelSub = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post("/payments/cancel");
      await refreshUser();
      setConfirmCancel(false);
      toast.success(data.message || "Auto-renewal turned off");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not cancel"); }
    finally { setCancelling(false); }
  };

  const input = "w-full bg-[#111726] border border-[#27334D] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500 transition-colors";

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto animate-fade-up" data-testid="account-page">
        <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Your account</h1>
        <p className="text-slate-400 mb-8">Manage your profile, password and subscription.</p>

        {/* Profile */}
        <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-6 mb-5">
          <div className="flex items-center gap-2.5 mb-5"><User className="w-4.5 h-4.5 text-blue-400" /><h2 className="font-display text-lg font-semibold">Profile</h2></div>
          <label className="block text-xs text-slate-500 font-mono-niq mb-1.5">Name</label>
          <div className="flex gap-2 mb-4">
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="account-name-input" className={input} />
            <button onClick={saveName} disabled={savingName} data-testid="account-name-save" className="shrink-0 bg-blue-600 hover:bg-blue-500 px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 flex items-center gap-2">
              {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </button>
          </div>
          <label className="block text-xs text-slate-500 font-mono-niq mb-1.5">Email</label>
          <div className="flex items-center gap-2 bg-[#0D121C] border border-[#27334D] rounded-lg px-3 py-2.5 text-sm text-slate-400">
            <Mail className="w-4 h-4 text-slate-500" /> {user?.email}
            {isGoogle && <span className="ml-auto text-[10px] uppercase tracking-wider font-mono-niq text-slate-500">Google sign-in</span>}
          </div>
        </div>

        {/* Password */}
        <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-6 mb-5">
          <div className="flex items-center gap-2.5 mb-5"><Lock className="w-4.5 h-4.5 text-blue-400" /><h2 className="font-display text-lg font-semibold">Change password</h2></div>
          {isGoogle ? (
            <p className="text-sm text-slate-400">You signed in with Google, so there's no password to change here. Manage your password in your Google account.</p>
          ) : (
            <div className="space-y-3">
              <input type="password" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="account-current-password" className={input} />
              <input type="password" placeholder="New password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="account-new-password" className={input} />
              <input type="password" placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="account-confirm-password" className={input} />
              <button onClick={changePassword} disabled={savingPw} data-testid="account-password-save" className="bg-blue-600 hover:bg-blue-500 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 flex items-center gap-2">
                {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </button>
            </div>
          )}
        </div>

        {/* Subscription */}
        <div className="rounded-2xl border border-[#27334D] bg-[#111726] p-6">
          <div className="flex items-center gap-2.5 mb-5"><CreditCard className="w-4.5 h-4.5 text-blue-400" /><h2 className="font-display text-lg font-semibold">Subscription</h2></div>
          <div className="flex items-center justify-between rounded-xl border border-[#27334D] bg-[#0D121C] px-4 py-3.5 mb-4">
            <div className="flex items-center gap-2.5">
              {isPaid ? <Sparkles className="w-4.5 h-4.5 text-blue-400" /> : <Check className="w-4.5 h-4.5 text-emerald-400" />}
              <div>
                <div className="font-medium text-sm" data-testid="account-plan-label">{isPaid ? `Pro plan${user?.plan_interval ? (user.plan_interval === "year" ? " · Annual" : " · Monthly") : ""}` : "Free plan"}</div>
                <div className="text-xs text-slate-500">{isPaid ? "Unlimited searches & downloads" : "3 searches · view-only"}</div>
              </div>
            </div>
            {isPaid && <span className="text-[10px] uppercase tracking-wider font-mono-niq text-emerald-300 bg-emerald-600/15 border border-emerald-500/30 px-2.5 py-1 rounded-full flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Active</span>}
          </div>

          {isPaid ? (
            willCancel ? (
              <div className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/[0.06] border border-amber-500/25 rounded-lg px-3 py-2.5" data-testid="account-cancel-scheduled">
                <AlertTriangle className="w-4 h-4 shrink-0" /> Auto-renewal is off. You keep Pro until the end of the current billing period.
              </div>
            ) : confirmCancel ? (
              <div className="rounded-lg border border-red-500/25 bg-red-500/[0.05] p-4">
                <p className="text-sm text-slate-300 mb-3">Turn off auto-renewal? You'll keep Pro access until your current period ends, then move to Free.</p>
                <div className="flex gap-2">
                  <button onClick={cancelSub} disabled={cancelling} data-testid="account-cancel-confirm" className="bg-red-600 hover:bg-red-500 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 flex items-center gap-2">
                    {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, cancel renewal"}
                  </button>
                  <button onClick={() => setConfirmCancel(false)} className="px-4 py-2 rounded-lg border border-[#27334D] hover:border-slate-500 text-sm">Keep Pro</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setConfirmCancel(true)} data-testid="account-cancel-button" className="text-sm text-red-400 hover:text-red-300 border border-red-500/25 hover:border-red-500/50 rounded-lg px-4 py-2.5 transition-colors">Cancel subscription</button>
            )
          ) : (
            <button onClick={() => navigate("/billing")} data-testid="account-upgrade-button" className="bg-blue-600 hover:bg-blue-500 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Upgrade to Pro
            </button>
          )}
        </div>
      </div>
    </AppShell>
  );
}
